import React, { useState } from 'react'
import type { TableColumnsType } from 'antd'
import { Button, Space, Table } from 'antd'
import { EditOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'

import { useUser } from '../../contexts'
import useCommercialSuppliers from '../../hooks/useCommercialSuppliers'
import { CommercialSupplier, RoleEnum } from '../../types'
import { StyledRow } from '../StyledComponents'
import { AddCommercialUserModal } from './AddCommercialUserModal'

const CommercialUsersView: React.FC = () => {
  const { t } = useTranslation()
  const { state: user } = useUser()
  const { commercialSuppliers, isLoading, refetch } = useCommercialSuppliers()
  const [modalOpen, setModalOpen] = useState(false)
  const [editingCommercialSupplierId, setEditingCommercialSupplierId] = useState<number | null>(
    null,
  )

  const isAdmin = user?.roles?.some(r => r.name === RoleEnum.ADMIN) ?? false

  const columns: TableColumnsType<CommercialSupplier> = [
    {
      title: t('commercial_users.name'),
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: t('commercial_users.surname'),
      dataIndex: 'surname',
      key: 'surname',
    },
    {
      title: t('commercial_users.email'),
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: t('commercial_users.cellphone'),
      dataIndex: 'cellphone',
      key: 'cellphone',
      render: (v: string | undefined) => v ?? '—',
    },
    {
      title: t('commercial_users.suppliers'),
      key: 'suppliers',
      render: (_: unknown, record: CommercialSupplier) => {
        const list = record.suppliers ?? []
        return list.map(s => s.tradeName).join(', ') || '—'
      },
    },
    {
      title: t('commercial_users.actions'),
      key: 'actions',
      width: 160,
      render: (_: unknown, record: CommercialSupplier) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => {
              setEditingCommercialSupplierId(record.id)
              setModalOpen(true)
            }}
          />
        </Space>
      ),
    },
  ]

  const rowClassName = (_record: unknown, index: number) =>
    index % 2 === 0 ? 'zebra-even' : 'zebra-odd'

  return (
    <>
      <StyledRow className="sticky-options border-botom">
        <div
          className="filters-container"
          style={{ display: 'flex', flexDirection: 'column', width: '100%' }}
        >
          <div
            className="filters-list"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '1rem',
              marginBottom: '1rem',
              marginLeft: '1rem',
            }}
          >
            {isAdmin && (
              <Button
                className="inline"
                type="primary"
                onClick={() => {
                  setEditingCommercialSupplierId(null)
                  setModalOpen(true)
                }}
                style={{ display: user?.roles[0].name === 'ADMIN' ? 'inline-block' : 'none' }}
                disabled={!(user?.roles[0].name === 'ADMIN')}
              >
                {t('commercial_users.add')}
              </Button>
            )}
          </div>
        </div>
        <Table<CommercialSupplier>
          rowKey="id"
          loading={isLoading}
          columns={columns}
          dataSource={commercialSuppliers}
          pagination={{ pageSize: 10 }}
          style={{ width: '100%' }}
          rowClassName={rowClassName}
        />
      </StyledRow>

      <AddCommercialUserModal
        open={modalOpen}
        editingCommercialSupplierId={editingCommercialSupplierId}
        onClose={() => {
          setModalOpen(false)
          setEditingCommercialSupplierId(null)
        }}
        onSuccess={() => refetch()}
      />
    </>
  )
}

export { CommercialUsersView }
